import { useEffect, useState } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const usePushNotifications = () => {
  const [isRegistered, setIsRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  
  // Check if push notifications are available on this platform
  const isPushAvailable = Capacitor.isPluginAvailable('PushNotifications');

  const checkPermission = async () => {
    if (!isPushAvailable) {
      return false;
    }
    
    try {
      const result = await PushNotifications.checkPermissions();
      return result.receive === 'granted';
    } catch (error) {
      console.error('Error checking push notification permissions:', error);
      return false;
    }
  };

  const registerNotifications = async () => {
    if (!isPushAvailable) {
      console.log('Push notifications not available on this platform');
      return false;
    }
    
    setIsLoading(true);
    try {
      // Request permission
      const permission = await PushNotifications.requestPermissions();
      
      if (permission.receive !== 'granted') {
        toast({
          title: "Άρνηση Πρόσβασης",
          description: "Χρειάζεται άδεια για push notifications",
          variant: "destructive",
        });
        setIsLoading(false);
        return false;
      }

      // Register with FCM
      await PushNotifications.register();
      
      return true;
    } catch (error) {
      console.error('Error registering for push notifications:', error);
      toast({
        title: "Σφάλμα",
        description: "Αποτυχία εγγραφής για push notifications",
        variant: "destructive",
      });
      setIsLoading(false);
      return false;
    }
  };

  const saveDeviceToken = async (token: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get platform
      const platform = /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'ios' : 'android';

      // Save token to database
      const { error } = await supabase
        .from('device_tokens')
        .upsert({
          user_id: user.id,
          token,
          platform,
        }, {
          onConflict: 'token',
        });

      if (error) {
        console.error('Error saving device token:', error);
        throw error;
      }

      setIsRegistered(true);
      setIsLoading(false);
      
      toast({
        title: "Επιτυχής Εγγραφή",
        description: "Θα λαμβάνετε push notifications",
      });
    } catch (error) {
      console.error('Error saving device token:', error);
      setIsLoading(false);
      toast({
        title: "Σφάλμα",
        description: "Αποτυχία αποθήκευσης device token",
        variant: "destructive",
      });
    }
  };

  const setupListeners = () => {
    if (!isPushAvailable) {
      return;
    }
    
    try {
      // Registration success
      PushNotifications.addListener('registration', (token) => {
        console.log('Push registration success, token:', token.value);
        saveDeviceToken(token.value);
      });

      // Registration error
      PushNotifications.addListener('registrationError', (error) => {
        console.error('Push registration error:', error);
        setIsLoading(false);
        toast({
          title: "Σφάλμα Εγγραφής",
          description: "Αποτυχία εγγραφής για push notifications",
          variant: "destructive",
        });
      });

      // Notification received (app in foreground)
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push notification received:', notification);
        toast({
          title: notification.title || "Νέα Ειδοποίηση",
          description: notification.body || "",
        });
      });

      // Notification clicked/tapped
      PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('Push notification action performed:', notification);
        
        // Navigate to link if provided
        const link = notification.notification.data?.link;
        if (link) {
          window.location.href = link.startsWith('http') ? link : link;
        }
      });
    } catch (error) {
      console.error('Error setting up push notification listeners:', error);
    }
  };

  const initialize = async () => {
    if (!isPushAvailable) {
      console.log('Push notifications not available on this platform');
      return;
    }
    
    try {
      const hasPermission = await checkPermission();
      if (hasPermission) {
        setIsRegistered(true);
        setupListeners();
      }
    } catch (error) {
      console.error('Error initializing push notifications:', error);
    }
  };

  useEffect(() => {
    initialize();
    
    return () => {
      if (isPushAvailable) {
        try {
          PushNotifications.removeAllListeners();
        } catch (error) {
          console.error('Error removing push notification listeners:', error);
        }
      }
    };
  }, []);

  return {
    isRegistered,
    isLoading,
    registerNotifications: async () => {
      const success = await registerNotifications();
      if (success) {
        setupListeners();
      }
    },
  };
};
